import {useEffect, useState} from 'react'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {useCurrentQueryRoute} from '@github-ui/react-core/future/use-current-query-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {setTitle} from '@github-ui/document-metadata'
import {useParams} from '@github-ui/react-router'

import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {ConversationHeader} from '@github-ui/copilot-shared-ui/components/ConversationHeader'
import {CurrentUserProvider} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {ReadOnlyLegacyChat} from '@github-ui/agent-sessions/components/legacy-chat/ReadOnlyLegacyChat'
import {useInitializeAuthToken} from '@github-ui/agent-sessions/hooks/use-initialize-auth-token'
import {useSSOChangeDetection} from '@github-ui/agent-sessions/hooks/use-sso-change-detection'
import type {AgentTasksAppPayload} from '@github-ui/agent-sessions/types/agent-tasks-app-payload'

import {AgentsSidebarLayout} from './AgentsSidebarLayout'
import {CopilotChatProviders} from './CopilotChatProviders'
import type {ChatPayload} from './copilot-chat-payload-route-query'
import styles from './CopilotAgentsConversationPage.module.css'

export function CopilotAgentsConversationPage() {
  const currentRoute = useCurrentQueryRoute()
  const {data: payload} = useRouteQuery(currentRoute, 'mainQuery')
  const chatPayloadQuery = useRouteQuery(currentRoute, 'copilotChatPayload')

  if (!payload) return null

  const typedPayload = payload as AgentTasksAppPayload
  const typedChatPayload = chatPayloadQuery.data as ChatPayload | undefined

  return (
    <CurrentUserProvider currentUser={typedPayload.currentUser} currentUserIsSessionOwner>
      <ConsecutiveCAPI401sProvider>
        <ErrorBoundary boundaryName="CopilotAgentsConversationPageContent">
          <CopilotAgentsConversationPageContent
            payload={typedPayload}
            chatPayload={typedChatPayload}
            isChatPayloadPending={chatPayloadQuery.isPending}
            isChatPayloadError={chatPayloadQuery.isError}
            onRetryChatPayload={() => void chatPayloadQuery.refetch()}
          />
        </ErrorBoundary>
      </ConsecutiveCAPI401sProvider>
    </CurrentUserProvider>
  )
}

function CopilotAgentsConversationPageContent({
  payload,
  chatPayload,
  isChatPayloadPending,
  isChatPayloadError,
  onRetryChatPayload,
}: {
  payload: AgentTasksAppPayload
  chatPayload: ChatPayload | undefined
  isChatPayloadPending: boolean
  isChatPayloadError: boolean
  onRetryChatPayload: () => void
}) {
  const {threadID} = useParams<{threadID: string}>()
  const [threadTitle, setThreadTitle] = useState<{threadID: string; title: string} | null>(null)

  useInitializeAuthToken(payload.initialAuthToken)
  useSSOChangeDetection(payload.initialAuthToken?.ssoOrgIDs)

  useEffect(() => {
    setTitle('Legacy chat · GitHub Copilot')
  }, [])

  return (
    <AgentsSidebarLayout
      agentsEnabled={!!payload.agentsEnabled}
      showSpacesNav={payload.spacesEnabled}
      showCustomizeNav={payload.customizeEnabled}
      currentUserLogin={payload.currentUser?.login}
      currentUserId={payload.currentUser?.id}
      enterpriseSandboxEnabled={payload.enterpriseSandboxEnabled !== false}
      taskGroups={payload.taskGroups}
      contentKey={threadID ?? ''}
      legacyChatPayload={chatPayload}
      chatApiURL={chatPayload?.apiURL}
      sidebarProps={{navigateToAgentsPage: true, activeThreadId: threadID}}
    >
      <SidebarPageLayout.Content as="div" padding="none">
        <div className={styles.content}>
          <div className={styles.conversation}>
            <div className={styles.header}>
              <SidebarPageLayout.SidebarTrigger />
              <div className={styles.titleSlot}>
                <ConversationHeader
                  title={
                    !isChatPayloadPending &&
                    !isChatPayloadError &&
                    chatPayload &&
                    threadTitle &&
                    threadTitle.threadID === threadID
                      ? threadTitle.title
                      : 'Legacy chat'
                  }
                />
              </div>
            </div>
            {isChatPayloadPending ? (
              <p>Loading legacy chat…</p>
            ) : isChatPayloadError ? (
              <div role="alert">
                <p>Couldn’t load legacy chat.</p>
                <button type="button" onClick={onRetryChatPayload}>
                  Retry
                </button>
              </div>
            ) : chatPayload ? (
              <CopilotChatProviders chatPayload={chatPayload}>
                <ReadOnlyLegacyChat payload={chatPayload} onTitleChange={setThreadTitle} />
              </CopilotChatProviders>
            ) : null}
          </div>
        </div>
      </SidebarPageLayout.Content>
    </AgentsSidebarLayout>
  )
}
